import { DateTime } from 'luxon';

import { isEmailBounceInterruptMessage } from '@poseidon/shared-utils/cadence';

import { Button, DateBoxButton } from './Input2';

type CadenceState =
  | 'not-started'
  | 'active'
  | 'paused'
  | 'finished'
  | 'interrupted'
  | 'action-required'
  | 'responded'
  | 'deleted'
  | 'researching'
  | 'pending-review'
  | 'rejected'
  | 'contact_unavailable';

type CadenceStepType =
  | 'message'
  | 'connect'
  | 'inmail'
  | 'visit'
  | 'follow'
  | 'email'
  | 'reminder'
  | 'call'
  | 'delay'
  | 'likeRecentPosts'
  | 'wait_for_connect'
  | 'voice_note'
  | 'video_note'
  | 'sift'
  | 'switch_campaign'
  | 'switch';

type ContactCardCadenceActionsProps = {
  hasResponse?: boolean;
  cadenceState?: CadenceState;
  cadenceRunning?: boolean;
  cadenceNextStepTypes?: CadenceStepType[];
  cadenceInterruptMessage?: string | null;
  firstReminderBody?: string | null;
  firstReminderDate?: Date;
  canNotRunStepTypes?: Set<CadenceStepType>;
  manuallyRunning?: boolean;
  errorMessage?: string;
  /** Pipeline cards fold the finished state into the campaign chip (blue + check),
   *  so they suppress this standalone FINISHED pill to avoid a duplicate badge. */
  hideFinished?: boolean;
};

export const ContactCardCadenceActions = ({
  hasResponse,
  cadenceState,
  cadenceRunning,
  cadenceNextStepTypes,
  cadenceInterruptMessage,
  firstReminderBody,
  firstReminderDate,
  canNotRunStepTypes,
  manuallyRunning,
  errorMessage,
  hideFinished,
}: ContactCardCadenceActionsProps) => {
  const isPaused = cadenceState === 'paused';
  const isInterrupted = cadenceState === 'interrupted';
  const isRunning = cadenceRunning === true || manuallyRunning;
  const isActionRequired = cadenceState === 'action-required';
  const isResponded = cadenceState === 'responded' || hasResponse;
  const isFinished = cadenceState === 'finished' && !hideFinished;
  const isResearching = cadenceState === 'researching';
  const isPendingReview = cadenceState === 'pending-review';

  const reminderDate = firstReminderDate ? DateTime.fromJSDate(firstReminderDate) : undefined;

  const tooltipMessage = reminderDate
    ? `Next reminder: ${firstReminderBody} ${reminderDate.toLocaleString(DateTime.DATE_MED)}`
    : undefined;

  const canRunTypes =
    canNotRunStepTypes == null ? cadenceNextStepTypes : cadenceNextStepTypes?.filter((t) => !canNotRunStepTypes.has(t));

  const canNotRunInCadence =
    canNotRunStepTypes == null ? [] : (cadenceNextStepTypes || []).filter((t) => canNotRunStepTypes.has(t));

  const limitText =
    (canRunTypes == null || canRunTypes.length <= 0) && canNotRunInCadence.length > 0
      ? canNotRunInCadence?.map((t) => `'${t}'`).join(', ')
      : undefined;

  const interruptedReason = errorMessage || cadenceInterruptMessage;

  return (
    <div className="flex flex-col items-end justify-between">
      <div className="flex items-start gap-1">
        {isRunning && (
          <Button
            size="2xs"
            color="green"
            readOnly
            tooltip="Campaign is running/stuck, retry"
            className="mt-0.5 animate-pulse">
            RUNNING
          </Button>
        )}

        {isPaused && (
          <Button size="2xs" color="purple" tone="outline" readOnly tooltip="Campaign is paused" className="mt-0.5">
            PAUSED
          </Button>
        )}

        {isFinished && (
          <Button size="2xs" color="blue" readOnly tooltip="Campaign is finished" className="mt-0.5">
            FINISHED
          </Button>
        )}

        {isActionRequired && (
          <Button
            size="2xs"
            color="purple"
            tone="outline"
            readOnly
            tooltip="Campaign requires your input"
            className="mt-0.5">
            NEEDS ACTION
          </Button>
        )}
        {isResponded && (
          <Button size="2xs" color="green" readOnly tooltip="Lead responded" className="mt-0.5">
            REPLY
          </Button>
        )}
        {isResearching && (
          <Button
            size="2xs"
            color="purple"
            tone="outline"
            readOnly
            tooltip="Researching contact via LinkedIn"
            className="mt-0.5 animate-pulse">
            RESEARCHING
          </Button>
        )}
        {isPendingReview && (
          <Button
            size="2xs"
            color="purple"
            tone="outline"
            readOnly
            tooltip="Research complete — review before starting"
            className="mt-0.5">
            PENDING REVIEW
          </Button>
        )}
        {isInterrupted && <InterruptedBadge interruptedReason={interruptedReason} />}

        {limitText && (
          <Button
            size="2xs"
            color="purple"
            tone="outline"
            readOnly
            tooltip={`Reached your daily limit for sending: ${limitText}`}
            className="mt-0.5">
            DAILY LIMIT REACHED
          </Button>
        )}
        {reminderDate && (
          <DateBoxButton
            size="md"
            month={reminderDate.toFormat('LLL').toUpperCase()}
            day={reminderDate.toFormat('d')}
            tooltip={tooltipMessage}
            readOnly
          />
        )}
      </div>
    </div>
  );
};

function InterruptedBadge({ interruptedReason }: { interruptedReason?: string | null }) {
  if (interruptedReason === 'Failed to type email for verification') {
    return (
      <Button size="2xs" color="yellow" tone="soft" readOnly tooltip={interruptedReason} className="mt-0.5">
        VERIFICATION EMAIL MISSING
      </Button>
    );
  }
  if (interruptedReason === "Can't message a non-connected person") {
    return (
      <Button size="2xs" color="yellow" tone="soft" readOnly tooltip={interruptedReason} className="mt-0.5">
        NOT CONNECTED
      </Button>
    );
  }

  if (interruptedReason === 'Contact does not have any professional emails set') {
    return (
      <Button size="2xs" color="yellow" tone="soft" readOnly tooltip={interruptedReason} className="mt-0.5">
        EMAIL MISSING
      </Button>
    );
  }

  if (interruptedReason === 'Contact has disabled conversations') {
    return (
      <Button size="2xs" color="yellow" tone="soft" readOnly tooltip={interruptedReason} className="mt-0.5">
        CONVERSATION DISABLED
      </Button>
    );
  }

  if (isEmailBounceInterruptMessage(interruptedReason)) {
    return (
      <Button size="2xs" color="yellow" tone="soft" readOnly tooltip={interruptedReason} className="mt-0.5">
        EMAIL BOUNCED
      </Button>
    );
  }

  if (interruptedReason === 'unresolved variable') {
    return (
      <Button
        size="2xs"
        color="yellow"
        tone="soft"
        readOnly
        tooltip="A personalization variable in this step couldn't be filled in for this contact (e.g. a missing custom field or a leftover placeholder). Edit the step or add the missing info, then resume."
        className="mt-0.5">
        MISSING INFO
      </Button>
    );
  }

  return (
    <Button size="2xs" color="red" tone="soft" readOnly tooltip={interruptedReason} className="mt-0.5">
      INTERRUPTED
    </Button>
  );
}
